import type { Status } from '@contracts/quickbooks/QuickbooksIntegrationContract';
import type {
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import QuickbooksLogo from '@src/common/assets/quickbooks/quickbooks-logo.svg?react';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import {
  tableContainer,
  tableStyle,
} from '@src/common/components/tableStyle/TableStyle';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import { useQuickbooksStatus } from '@src/quickbooks/hooks/useQuickbooksStatus';
import { QuickbooksButton } from '@src/quickbooks/QuickbooksButton';
import { useTranslation } from 'react-i18next';

export const QuickbooksSettings = () => {
  const { t } = useTranslation();

  const { status, isLoading } = useQuickbooksStatus();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('integrations.name'),
      flex: 3,
      renderCell: (params) => params.value,
    },
    {
      field: 'status',
      headerName: t('integrations.status'),
      flex: 2,
      renderCell: StatusBadgeCell,
    },
    {
      field: 'lastConnection',
      headerName: t('integrations.lastConnection'),
      flex: 3,
    },
    {
      field: 'action',
      headerName: '',
      flex: 2,
      align: 'center',
      renderCell: (params) => params.value,
    },
  ];

  const rows = () => [
    {
      id: 'quickbooks',
      name: <QuickbooksLogo style={{ height: 'inherit', width: 'auto' }} />,
      status: status?.status,
      lastConnection: status?.lastConnection
        ? new Timestamp(status.lastConnection).formatDate('dd/MM/yyyy')
        : '',
      action: <QuickbooksButton status={status?.status} />,
    },
  ];

  return (
    <Stack
      flex="1"
      paddingTop="0px"
      className={tableContainer}
      style={{ paddingTop: 0 }}
    >
      <DataGridPro
        columns={columns}
        rows={isLoading ? [] : rows()}
        className={tableStyle}
        hideFooter={true}
        style={{ borderRadius: theme.radius.around }}
        headerFilters={false}
        disableColumnSorting={true}
        disableColumnMenu={true}
        rowSelection={false}
        disableColumnResize={true}
        loading={isLoading}
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.surface.surfaceContainerLowest,
          },
        }}
      />
    </Stack>
  );
};

const StatusBadgeCell = (
  params: GridRenderCellParams<GridValidRowModel, Status>,
) => {
  const { t } = useTranslation();
  if (params.value === 'active')
    return (
      <StatusBadge
        text={t('integrations.active')}
        color={theme.palette.success.success}
        borderColor={theme.palette.success.success}
        backgroundColor={theme.palette.success.successContainer}
        size="medium"
      />
    );
  if (params.value === 'expired')
    return (
      <StatusBadge
        text={t('integrations.expired')}
        color={theme.palette.warning.warning}
        borderColor={theme.palette.warning.border}
        backgroundColor={theme.palette.warning.warningContainer}
        size="medium"
      />
    );
  return '';
};
